import { baseApi } from "./base.api";

export interface Agent {
  id: string;
  agent_name: string;
  is_deployed: boolean;
  temperature: number;
  model_name: string;
  created_at: string;
  role?: {
    id: string;
    name: string;
  };
}

export interface AgentCreateRequest {
  agentName: string;
  modelName: string;
}

export interface AgentCreateResponse {
  successMessage: string;
  agentId: string;
}

export interface AgentWidgetConfig {
  id: string;
  ai_agent: Agent;
  agent_icon: string;
  payment_plan: string;
  size: number;
  placement: string;
  main_color: string;
  text_color: string;
}

export interface TrainingDataCreateRequest {
  sourceType: "text" | "file" | "web" | "gdrive";
  trainingDataFile?: File;
  driveCredentialPath?: string;
  fileId?: string;
  websiteUrls?: string[];
  agentId: string;
  textPrompt?: string;
}

export interface TrainingDataCreateResponse {
  successMessage: string;
}

export interface UpdateAgentWidgetConfig extends Partial<AgentWidgetConfig> {
  welcomeMsg?: string;
  isHMMobileEnabled?: boolean;
  isHMDesktopEnabled?: boolean;
  hoveringMessages?: string[];
  mainColor?: string;
  textColor?: string;
  widgetSize?: string;
  widgetPlacement?: string;
  agentIcon?: string;
  isAOCMobileEnabled?: boolean;
  isAOCDesktopEnabled?: boolean;
  delayDuration?: string;
  widgetCornerRadius?: number;
  reqType: "texts" | "customize" | "configure";
}

export interface UpdateAgent {
  agentName?: string;
  isDeployed?: boolean;
  temperature?: number;
  modelName?: string;
}

export interface TrainingDocument {
  id: string;
  document: string;
  name: string;
}

export interface TrainingWebsite {
  id: string;
  website_url: string;
}

export interface TrainingText {
  id: string;
  text_prompt: string;
}

export interface AgentTrainingDataResponse {
  training_documents: TrainingDocument[];
  training_websites: TrainingWebsite[];
  training_text: TrainingText[];
}

export interface AgentRole {
  id: string;
  name: string;
}

export interface AgentTemplate {
  id: string;
  agent_name: string;
  role?: string;
  agent_icon?: string;
}

export interface TrainingSummary {
  training_data_summary: {
    company_overview: string;
    product_summary: string;
    features: string[];
    pricing_models: string;
    onboarding_steps: string[];
    gaps_in_information: string[];
    additional_notes: string[];
    objections: string[];
  };
}

export interface Conversation {
  id: string;
  chat_heading: string;
  most_recent_message: string;
  created_at: string;
}

export const agentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgents: builder.query<Agent[], void>({
      query: () => ({
        url: "/agent/",
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Agent" as const, id })),
              { type: "Agent" as const, id: "LIST" },
            ]
          : [{ type: "Agent" as const, id: "LIST" }],
    }),
    getAgentConversation: builder.mutation<Conversation[], string>({
      query: (id) => ({
        url: "/agent/chat",
        method: "POST",
        body: { agentId: id },
      }),
      invalidatesTags: ["Agent"],
      transformResponse: (r: { chat_contexts: Conversation[] }) =>
        r.chat_contexts,
    }),

    transcribe: builder.mutation<{ text: string }, FormData>({
      query: (data) => ({
        url: "/agent/transcribe",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Agent", id: "LIST" }],
    }),

    createAgent: builder.mutation<AgentCreateResponse, AgentCreateRequest>({
      query: (data) => ({
        url: "/agent/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Agent", id: "LIST" }],
    }),

    getSiteLink: builder.mutation<{ links: string[] }, { url: string }>({
      query: (data) => ({
        url: "/agent/training_data/links",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Agent"],
    }),

    updateAgent: builder.mutation<
      Partial<Agent>,
      UpdateAgent & { id: string; headers?: Record<string, string> }
    >({
      query: (agent) => {
        const { id, headers } = agent;
        return {
          url: `/agent/modify/${id}`,
          method: "PATCH",
          body: agent,
          headers,
          };
        },
        invalidatesTags: (result, error, { id }) => [
          { type: "Agent", id: "LIST" },
          { type: "Agent", id },
          { type: "WidgetConfig", id },
        ],
      }
    ),

    getAgentWidgetConfig: builder.query<AgentWidgetConfig, string>({
      query: (agent_id) => ({
        url: `/agent/widget_config/${agent_id}`,
        method: "GET",
      }),
      providesTags: (result, error, agent_id) => [
        { type: "WidgetConfig" as const, id: agent_id },
      ],
    }),

    updateAgentWidget: builder.mutation<
      { successMessage: string; widgetConfig: AgentWidgetConfig },
      {
        agent_id: string;
        data: UpdateAgentWidgetConfig | FormData;
      }
    >({
      query: ({ agent_id, data }) => ({
        url: `/agent/widget/${agent_id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { agent_id }) => [
        { type: "WidgetConfig", id: agent_id },
        { type: "Agent", id: agent_id },
      ],
    }),

    deleteAgent: builder.mutation<{ successMessage: string }, string>({
      query: (agent_id) => ({
        url: `/agent/modify/${agent_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, agent_id) => [
        { type: "Agent", id: "LIST" },
        { type: "Agent", id: agent_id },
        { type: "WidgetConfig", id: agent_id },
      ],
    }),

    getAgentTrainingData: builder.query<AgentTrainingDataResponse, string>({
      query: (id) => ({
        url: `/agent/training_data/${id}`,
        method: "GET",
      }),
      providesTags: [{ type: "TrainingData", id: "LIST" }],
    }),

    createAgentTrainingData: builder.mutation<
      TrainingDataCreateResponse,
      Partial<TrainingDataCreateRequest> | FormData
    >({
      query: (data) => ({
        url: "/agent/training_data",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "TrainingData", id: "LIST" }],
    }),

    deleteTrainingData: builder.mutation<
      { successMessage: string },
      {
        agent_id: string;
        documentId: string;
        documentType: "text" | "file" | "website";
      }
    >({
      query: ({ agent_id, documentId, documentType }) => ({
        url: `/agent/training_data/${agent_id}`,
        method: "DELETE",
        body: { documentId, documentType },
      }),
      invalidatesTags: [{ type: "TrainingData", id: "LIST" }],
    }),

    getAgentRoles: builder.query<AgentRole[], void>({
      query: () => ({
        url: "/agent/roles",
        method: "GET",
      }),
    }),

    getAgentTemplates: builder.query<AgentTemplate[], void>({
      query: () => ({
        url: "/agent/template",
        method: "GET",
      }),
    }),

    createAgentFromTemplate: builder.mutation<
      AgentCreateResponse,
      { templateId: string }
    >({
      query: (data) => ({
        url: "/agent/template/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Agent", id: "LIST" }],
    }),

    getTrainingSummary: builder.query<TrainingSummary, string>({
      query: (agent_id) => ({
        url: `/agent/training_summary/${agent_id}`,
        method: "GET",
      }),
    }),

    getAgentVoices: builder.query<string[], void>({
      query: () => ({
        url: "/agent/voice",
        method: "GET",
      }),
    }),

    getAllWidgetConfigs: builder.query<AgentWidgetConfig[], void>({
      query: () => ({
        url: "/agent/widget_config",
        method: "GET",
      }),
      providesTags: ["WidgetConfig"],
    }),
  }),
});

export const {
  useGetAgentsQuery,
  useCreateAgentMutation,
  useGetAgentWidgetConfigQuery,
  useUpdateAgentWidgetMutation,
  useDeleteAgentMutation,
  useGetAgentTrainingDataQuery,
  useCreateAgentTrainingDataMutation,
  useTranscribeMutation,
  useUpdateAgentMutation,
  useGetAgentConversationMutation,
  useGetSiteLinkMutation,
  useDeleteTrainingDataMutation,
  useGetAgentRolesQuery,
  useGetAgentTemplatesQuery,
  useCreateAgentFromTemplateMutation,
  useGetTrainingSummaryQuery,
  useGetAgentVoicesQuery,
  useGetAllWidgetConfigsQuery,
} = agentApi;
